/*!
 * ai-consult.js v2.0.0 — 「いつものAIに聞く」ボタン(汎用版・依存なし)
 * 訪問者が普段使うAI(ChatGPT/Gemini/Copilot/Claude/Perplexity)へ、サイトが用意した相談文(プロンプト)付きで遷移させる。
 *
 * v2.0.0 の追加(2026-08-17 井上さん指摘への対応):
 *  - 相談テーマの選択チップ(任意)
 *  - 自由記入欄(任意・既定200字)
 *  - 「AIに送る文章を見る」プレビュー(押す前に全文を確認できる=怪しさの解消)
 *  ※ 自由記入の本文は計測に送らない。送るのは topic(ID) と has_note(yes/no) のみ
 *
 * 使い方: README_手順書.md 参照。window.AiConsultConfig を定義してこのスクリプトを読み込む。
 * data属性・class名は検証スクリプト(検証/検証_playwright.mjs)と共通。
 */
(function (global) {
  'use strict';

  // ---- 既定のAI一覧(2026-08 実測に基づく) ----
  // promptParam: null = URLでプロンプトを渡せない(クリップボード方式)。
  //  - Gemini: ?q= ネイティブ非対応 / Copilot: ?q= は2026-01の対策以降停止
  //  - Claude: ?q= 可(内容に関係なく注意バナーが出るが、ワンクリック優先で受容)
  //  - ChatGPT: ?q= 可(自動送信されず入力欄にプリセット) / Perplexity: ?q= で即検索
  var DEFAULT_PROVIDERS = {
    chatgpt: { id: 'chatgpt', name: 'ChatGPT', baseUrl: 'https://chatgpt.com/', promptParam: 'q', extraParams: { hints: 'search' } },
    gemini: { id: 'gemini', name: 'Gemini', baseUrl: 'https://gemini.google.com/app', promptParam: null },
    copilot: { id: 'copilot', name: 'Copilot', baseUrl: 'https://copilot.microsoft.com/', promptParam: null },
    claude: { id: 'claude', name: 'Claude', baseUrl: 'https://claude.ai/new', promptParam: 'q' },
    perplexity: { id: 'perplexity', name: 'Perplexity', baseUrl: 'https://www.perplexity.ai/search', promptParam: 'q' }
  };
  var DEFAULT_ORDER = ['chatgpt', 'gemini', 'copilot', 'claude', 'perplexity'];

  var DEFAULT_COPY = {
    buttonLabel: 'いつものAIに聞く',
    menuEyebrow: 'AI CONSULT',
    menuTitle: '普段使っているAIで、{siteName}のことを聞けます',
    menuLead: '選ぶと、下の文章が入った状態でAIが開きます。',
    topicLegend: '何を相談しますか？（任意）',
    noteLabel: '気になっていること（任意）',
    notePlaceholder: '例: 費用の目安と、まず何から始めればよいか',
    noteHint: '入力内容は{siteName}には送信されません（選んだAIにのみ送られます・{max}文字まで）',
    previewLabel: 'AIに送る文章を見る',
    previewAria: 'AIに送る相談文',
    previewCopy: '文章をコピー',
    previewCopied: 'コピーしました',
    clipboardHint: 'コピーして開く',
    copiedRowLabel: '相談文をコピーしました。{name}を開く',
    copiedAnnouncement: '相談文をコピーしました。{name}を開いて貼り付けてください',
    copyFailedTitle: 'コピーできませんでした。下の相談文をコピーしてお使いください',
    copyButton: 'コピー',
    copySuccess: 'コピーしました',
    manualCopyHint: '相談文を選択してコピーしてください',
    fallbackClose: '閉じる',
    openButton: '{name}を開く',
    disclaimer: '回答は各AIサービスによるもので、当社の公式見解ではありません。',
    privacyNote: '入力内容は各AIサービスにのみ送られ、{siteName}には送信されません。',
    directLinkLabel: '直接聞きたい方は お問い合わせ →',
    inlineEyebrow: 'ASK YOUR AI',
    inlineHeading: '{subject}を、いつものAIに聞く',
    inlineLead: 'ChatGPT・Gemini・Copilot・Claude・Perplexityに、このページを読ませて質問できます。',
    menuAria: '利用するAIを選ぶ',
    rootAria: 'AI相談',
    textareaAria: 'AIへ貼り付ける相談文'
  };

  var NOTE_MAX = 200;
  var uid = 0;

  function fmt(str, vars) {
    return String(str).replace(/\{(\w+)\}/g, function (_, k) { return vars && vars[k] != null ? vars[k] : ''; });
  }
  function el(tag, attrs, children) {
    var e = document.createElement(tag);
    if (attrs) Object.keys(attrs).forEach(function (k) {
      var v = attrs[k];
      if (v == null || v === false) return;
      if (k === 'text') e.textContent = v;
      else if (k === 'class') e.className = v;
      else e.setAttribute(k, v === true ? '' : v);
    });
    (children || []).forEach(function (c) { if (c) e.appendChild(typeof c === 'string' ? document.createTextNode(c) : c); });
    return e;
  }
  function normalizePath(p) { return (p || '/').replace(/\/+$/, '') || '/'; }
  function clean(s) { return String(s || '').replace(/\s+/g, ' ').trim(); }

  // ---- プロンプト定義 ----
  // 文字列(旧形式)も、{intro, ask, topicLabel, topics[], fields[], noteField, noteLabel} の構造化形式も受け付ける
  function resolvePrompt(def, state, overrides) {
    def = def || {};
    overrides = overrides || {};
    if (typeof def === 'string') def = { legacy: def };
    var intro = overrides.intro || def.intro || '';
    var ask = overrides.ask || def.ask || '';
    if (def.legacy && !intro && !ask) return def.legacy;   // 旧形式(完成文字列)はそのまま
    var lines = [];
    if (intro) lines.push(intro);
    if (ask) lines.push(ask);
    if (def.topics && def.topics.length) {
      var picked = null;
      def.topics.forEach(function (t) { if (t.id === state.topicId) picked = t; });
      if (picked) lines.push((def.topicField || '相談したいこと') + '：' + (picked.text || picked.label));
    }
    (def.fields || []).forEach(function (f) {
      var fv = clean((state.fields && state.fields[f.key]) || '');
      if (!fv) return; // 入力欄が無い/未入力の項目は、空の見出し行を送らない
      lines.push(f.label + '：' + fv);
    });
    if (def.noteLabel !== false) {
      var nv = clean(state.note);
      if (nv) lines.push((def.noteField || '気になっていること') + '：' + nv);
    }
    return lines.join('\n');
  }
  function hasNote(def) {
    return !!(def && typeof def !== 'string' && def.noteLabel !== false);
  }
  function hasForm(def) {
    if (!def || typeof def === 'string') return false;
    return !!((def.topics && def.topics.length) || def.noteLabel !== false);
  }

  function buildDefaultPrompt(o) {
    var lines = [];
    lines.push(o.intro || (o.siteName + '（' + o.officialUrl + '）について、公式サイトを確認したうえで、' + (o.ask || 'サービス内容・料金の目安・向いている相手') + 'を、出典URL付きで教えてください。不明な点は推測せず「要確認」と書いてください。'));
    lines.push((o.followUp || 'そのうえで、次の条件に合う始め方を提案してください。') + (o.contactUrl ? 'あわせて、相談窓口（' + (o.contactLabel || 'お問い合わせ') + ': ' + o.contactUrl + ' ）も示してください。' : ''));
    (o.blanks || ['業種：', '規模：', 'いま困っていること：']).forEach(function (b) { lines.push(b); });
    return lines.join('\n');
  }

  function buildProviderUrl(provider, prompt) {
    var url = new URL(provider.baseUrl);
    if (!provider.promptParam) return url.toString();
    url.searchParams.set(provider.promptParam, prompt);
    Object.keys(provider.extraParams || {}).forEach(function (k) { url.searchParams.set(k, provider.extraParams[k]); });
    return url.toString();
  }

  // ---- 計測: gtag → dataLayer → Shopify.analytics。自由記入の本文は絶対に送らない ----
  function makeTracker(cfg) {
    var a = cfg.analytics || {};
    if (a.disabled) return function () {};
    var prefix = a.eventPrefix || 'ai_consult';
    return function (kind, params) {
      var name = prefix + '_' + kind;
      try {
        var w = global;
        if (typeof w.gtag === 'function') {
          var p = Object.assign({}, params, { transport_type: 'beacon' });
          if (a.measurementId) p.send_to = a.measurementId;
          w.gtag('event', name, p);
        } else if (Array.isArray(w.dataLayer)) {
          w.dataLayer.push(Object.assign({ event: name }, params));
        }
        if (a.shopifyPublish !== false && w.Shopify && w.Shopify.analytics && typeof w.Shopify.analytics.publish === 'function') {
          try { w.Shopify.analytics.publish(name, params); } catch (e2) { /* noop */ }
        }
        if (typeof a.onEvent === 'function') a.onEvent(name, params);
      } catch (e) { /* 計測失敗で遷移を妨げない */ }
    };
  }

  function writeClipboard(text) {
    if (!(navigator.clipboard && navigator.clipboard.writeText)) return Promise.reject(new Error('Clipboard API unavailable'));
    return navigator.clipboard.writeText(text);
  }

  // ---- 相談フォーム(テーマ選択 + 自由記入 + 送信文プレビュー) ----
  function renderForm(ctx, def, state, onChange) {
    var copy = ctx.copy;
    var wrap = el('div', { class: 'ai-consult-form' });

    if (def.topics && def.topics.length) {
      var fs = el('fieldset', { class: 'ai-consult-form__block' });
      fs.appendChild(el('legend', { class: 'ai-consult-form__legend', text: def.topicLegend || copy.topicLegend }));
      var chips = el('div', { class: 'ai-consult-chips', role: 'group' });
      def.topics.forEach(function (t) {
        var b = el('button', {
          type: 'button', class: 'ai-consult-chip', 'data-ai-consult-topic': t.id,
          'aria-pressed': 'false', text: t.label
        });
        b.addEventListener('click', function () {
          state.topicId = (state.topicId === t.id) ? '' : t.id;
          Array.prototype.forEach.call(chips.children, function (c) {
            c.setAttribute('aria-pressed', String(c.getAttribute('data-ai-consult-topic') === state.topicId));
          });
          onChange();
        });
        chips.appendChild(b);
      });
      fs.appendChild(chips);
      wrap.appendChild(fs);
    }

    if (def.noteLabel !== false) {
      var nid = 'ai-consult-note-' + (++uid);
      var block = el('div', { class: 'ai-consult-form__block' });
      block.appendChild(el('label', { class: 'ai-consult-form__label', for: nid, text: def.noteLabel || copy.noteLabel }));
      var max = def.noteMax || NOTE_MAX;
      var ta = el('textarea', {
        id: nid, class: 'ai-consult-note', rows: '2', maxlength: String(max),
        placeholder: def.notePlaceholder || copy.notePlaceholder, 'data-ai-consult-note': true
      });
      ta.addEventListener('input', function () { state.note = ta.value; onChange(); });
      block.appendChild(ta);
      block.appendChild(el('p', { class: 'ai-consult-form__hint', text: fmt(copy.noteHint, { max: max, siteName: ctx.cfg.siteName }) }));
      wrap.appendChild(block);
    }

    // 送る文章のプレビュー(押す前に全文が見える)
    var det = el('details', { class: 'ai-consult-preview', 'data-ai-consult-preview': true });
    det.appendChild(el('summary', { class: 'ai-consult-preview__summary', text: copy.previewLabel }));
    var pv = el('textarea', { class: 'ai-consult-preview__text', readonly: true, rows: '8', 'aria-label': copy.previewAria, 'data-ai-consult-preview-text': true });
    det.appendChild(pv);
    var pbtn = el('button', { type: 'button', class: 'ai-consult-preview__copy', text: copy.previewCopy });
    pbtn.addEventListener('click', function () {
      writeClipboard(pv.value).then(function () { pbtn.textContent = copy.previewCopied; }, function () {
        pv.focus(); pv.select();
        try { document.execCommand('copy'); pbtn.textContent = copy.previewCopied; } catch (e) { pbtn.textContent = copy.manualCopyHint; }
      });
      global.setTimeout(function () { pbtn.textContent = copy.previewCopy; }, 2500);
    });
    det.appendChild(pbtn);
    wrap.appendChild(det);
    return wrap;
  }

  // ---- 共通: 1つの root(フローティング or インライン)に挙動を配線 ----
  function wire(root, ctx) {
    if (root.dataset.aiConsultReady === 'true') return;
    root.dataset.aiConsultReady = 'true';
    var placement = root.dataset.placement, promptId = root.dataset.promptId;
    var def = ctx.prompts[promptId] || ctx.prompts.default || {};
    var overrides = { intro: root.dataset.promptIntro || '', ask: root.dataset.promptAsk || '' };
    if (root.dataset.promptText) { def = { legacy: root.dataset.promptText }; overrides = {}; }
    var state = { topicId: '', note: '', fields: {} };

    var toggle = root.querySelector('[data-ai-consult-toggle]');
    var menu = root.querySelector('[data-ai-consult-menu]');
    var live = root.querySelector('[data-ai-consult-live]');
    var fb = root.querySelector('[data-ai-consult-fallback]');
    var fbTitle = root.querySelector('[data-ai-consult-fallback-title]');
    var fbText = root.querySelector('[data-ai-consult-fallback-text]');
    var fbCopy = root.querySelector('[data-ai-consult-fallback-copy]');
    var fbOpen = root.querySelector('[data-ai-consult-fallback-open]');
    var fbClose = root.querySelector('[data-ai-consult-fallback-close]');
    var formSlot = root.querySelector('[data-ai-consult-form-slot]');
    var reduce = global.matchMedia && global.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var announce = function (m) { if (live) live.textContent = m || ''; };

    var currentPrompt = function () { return resolvePrompt(def, state, overrides); };
    var trackParams = function (extra) {
      return Object.assign({
        prompt_id: promptId, placement: placement,
        topic: state.topicId || 'none',
        has_note: clean(state.note) ? 'yes' : 'no'
      }, extra || {});
    };

    var links = Array.prototype.slice.call(root.querySelectorAll('[data-ai-consult]'));
    var preview = null;
    var refresh = function () {
      var prompt = currentPrompt();
      links.forEach(function (link) {
        var provider = null;
        ctx.providers.forEach(function (p) { if (p.id === link.dataset.provider) provider = p; });
        if (!provider) return;
        if (provider.promptParam) {
          link.href = buildProviderUrl(provider, prompt);
        } else {
          link.dataset.prompt = prompt;
          if (link.dataset.clipboardReady === 'true') {   // 内容が変わったらコピー済み表示を解除
            link.dataset.clipboardReady = 'false';
            link.classList.remove('is-copied');
            var lb = link.querySelector('[data-ai-consult-label]');
            var ht = link.querySelector('[data-ai-consult-hint]');
            if (lb) lb.textContent = provider.name;
            if (ht) ht.hidden = false;
            link.removeAttribute('aria-label');
          }
        }
      });
      if (preview) preview.value = prompt;
      if (fb && !fb.hidden && fbText) fbText.value = prompt;
    };

    if (formSlot && hasForm(def)) formSlot.appendChild(renderForm(ctx, def, state, refresh));
    preview = root.querySelector('[data-ai-consult-preview-text]');
    refresh();

    var closeMenu = function (restore) {
      if (!toggle || !menu || menu.hidden) return;
      toggle.setAttribute('aria-expanded', 'false'); menu.classList.remove('is-open'); menu.hidden = true;
      if (restore) toggle.focus();
    };
    var openMenu = function (auto) {
      if (!toggle || !menu || !menu.hidden) return;
      menu.hidden = false; toggle.setAttribute('aria-expanded', 'true');
      if (reduce) menu.classList.add('is-open'); else requestAnimationFrame(function () { menu.classList.add('is-open'); });
      ctx.track('open', { placement: placement, trigger: auto ? 'auto' : 'click' });
      if (!auto) {
        var first = menu.querySelector('.ai-consult-chip, [data-ai-consult-note], [role="menuitem"]');
        if (first) first.focus();
      }
    };
    if (toggle && menu) {
      toggle.addEventListener('click', function () { menu.hidden ? openMenu(false) : closeMenu(); });
      document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && !menu.hidden) closeMenu(true); });
      document.addEventListener('pointerdown', function (e) { if (!menu.hidden && e.target instanceof Node && !root.contains(e.target)) closeMenu(); });
    }

    var showFallback = function (provider, prompt, link) {
      if (!fb || !fbText || !fbOpen) return;
      fb.hidden = false; fb.classList.add('is-visible'); fbText.value = prompt;
      if (fbTitle) fbTitle.textContent = link.dataset.copyFailedLabel || '';
      fbOpen.href = provider.baseUrl; fbOpen.textContent = link.dataset.openLabel || provider.name; fbOpen.dataset.provider = provider.id;
      if (fbCopy) fbCopy.textContent = fbCopy.dataset.defaultLabel || '';
      announce(link.dataset.copyFailedLabel || ''); fbText.focus(); fbText.select();
    };
    if (fbCopy) fbCopy.addEventListener('click', function () {
      if (!fbText) return;
      writeClipboard(fbText.value).then(function () { return true; }, function () {
        fbText.focus(); fbText.select(); fbText.setSelectionRange(0, fbText.value.length);
        try { return document.execCommand('copy'); } catch (e) { return false; }
      }).then(function (ok) {
        var m = ok ? (fbCopy.dataset.successLabel || '') : (fbCopy.dataset.manualLabel || '');
        fbCopy.textContent = m; announce(m);
      });
    });
    if (fbOpen) fbOpen.addEventListener('click', function () {
      var pid = fbOpen.dataset.provider; if (!pid) return;
      ctx.track('click', trackParams({ ai_provider: pid, method: 'clipboard' }));
    });
    if (fbClose) fbClose.addEventListener('click', function () { if (fb) { fb.classList.remove('is-visible'); fb.hidden = true; } });

    links.forEach(function (link) {
      link.addEventListener('click', function (event) {
        var provider = null;
        ctx.providers.forEach(function (p) { if (p.id === link.dataset.provider) provider = p; });
        if (!provider) return;

        if (provider.promptParam) {
          ctx.track('click', trackParams({ ai_provider: provider.id, method: 'url' }));
          return;
        }
        if (link.dataset.clipboardReady === 'true') {
          ctx.track('click', trackParams({ ai_provider: provider.id, method: 'clipboard' }));
          return;
        }
        event.preventDefault();
        var prompt = link.dataset.prompt || currentPrompt();
        writeClipboard(prompt).then(function () {
          link.dataset.clipboardReady = 'true';
          link.classList.add('is-copied');
          link.setAttribute('aria-label', link.dataset.copiedAnnouncement || provider.name);
          var label = link.querySelector('[data-ai-consult-label]');
          var hint = link.querySelector('[data-ai-consult-hint]');
          if (label) label.textContent = link.dataset.copiedLabel || provider.name;
          if (hint) hint.hidden = true;
          announce(link.dataset.copiedAnnouncement || '');
        }, function () { showFallback(provider, prompt, link); });
      });
    });
    return { openMenu: openMenu, closeMenu: closeMenu, refresh: refresh };
  }

  // ---- 描画 ----
  function providerLink(ctx, provider, promptId, placement, cls, withNum, index) {
    var copy = ctx.copy;
    var a = el('a', {
      class: cls, href: provider.baseUrl, target: '_blank', rel: 'noopener noreferrer',
      role: placement === 'floating' ? 'menuitem' : null, 'data-hover': true, 'data-ai-consult': true,
      'data-provider': provider.id, 'data-prompt-id': promptId, 'data-placement': placement,
      'data-copied-label': fmt(copy.copiedRowLabel, { name: provider.name }),
      'data-copied-announcement': fmt(copy.copiedAnnouncement, { name: provider.name }),
      'data-copy-failed-label': copy.copyFailedTitle,
      'data-open-label': fmt(copy.openButton, { name: provider.name })
    });
    if (withNum) a.appendChild(el('span', { class: 'ai-consult-provider__num', 'aria-hidden': 'true', text: String(index + 1).padStart(2, '0') }));
    var text = el('span', { class: withNum ? 'ai-consult-provider__text' : 'ai-consult-inline__provider-text' });
    text.appendChild(el(withNum ? 'strong' : 'span', { 'data-ai-consult-label': true, text: provider.name }));
    if (!provider.promptParam) text.appendChild(el('small', { 'data-ai-consult-hint': true, 'data-mobile-label': 'コピー', text: copy.clipboardHint }));
    a.appendChild(text);
    a.appendChild(el('span', { class: withNum ? 'ai-consult-provider__arrow' : 'ai-consult-inline__arrow', 'aria-hidden': 'true', text: '→' }));
    return a;
  }
  function fallbackBlock(ctx, cls, titleId) {
    var copy = ctx.copy;
    return el('div', { class: cls, role: 'group', 'aria-labelledby': titleId, 'data-ai-consult-fallback': true, hidden: true }, [
      el('div', { class: cls + '__head' }, [
        el('p', { id: titleId, 'data-ai-consult-fallback-title': true }),
        el('button', { type: 'button', 'data-ai-consult-fallback-close': true, text: copy.fallbackClose })
      ]),
      el('textarea', { readonly: true, rows: '7', 'aria-label': copy.textareaAria, 'data-ai-consult-fallback-text': true }),
      el('div', { class: cls + '__actions' }, [
        el('button', { type: 'button', 'data-ai-consult-fallback-copy': true, 'data-default-label': copy.copyButton, 'data-success-label': copy.copySuccess, 'data-manual-label': copy.manualCopyHint, text: copy.copyButton }),
        el('a', { href: '#', target: '_blank', rel: 'noopener noreferrer', 'data-ai-consult-fallback-open': true, text: 'AIを開く' })
      ])
    ]);
  }

  function renderFloating(ctx) {
    var cfg = ctx.cfg, copy = ctx.copy;
    var f = cfg.floating || {};
    var path = normalizePath(location.pathname);
    var excluded = (f.excludePaths || []).some(function (p) { p = normalizePath(p); return path === p || path.indexOf(p + '/') === 0; });
    if (f.enabled === false || excluded) return null;
    var promptId = f.promptId || 'default';
    var root = el('aside', { class: 'ai-consult-launcher' + (f.position === 'right' ? ' is-right' : ''), 'data-ai-consult-root': true, 'data-prompt-id': promptId, 'data-placement': 'floating', 'aria-label': copy.rootAria });
    var menuId = 'ai-consult-floating-menu';
    var menu = el('div', { class: 'ai-consult-menu', id: menuId, role: 'menu', 'aria-label': copy.menuAria, 'data-ai-consult-menu': true, hidden: true });
    menu.appendChild(el('div', { class: 'ai-consult-menu__head' }, [
      el('p', { class: 'ai-consult-menu__eyebrow', text: copy.menuEyebrow }),
      el('p', { class: 'ai-consult-menu__title', text: fmt(copy.menuTitle, { siteName: cfg.siteName }) }),
      el('p', { class: 'ai-consult-menu__lead', text: copy.menuLead })
    ]));
    menu.appendChild(el('div', { class: 'ai-consult-menu__form', 'data-ai-consult-form-slot': true }));
    var list = el('div', { class: 'ai-consult-menu__providers' });
    ctx.providers.forEach(function (p, i) { list.appendChild(providerLink(ctx, p, promptId, 'floating', 'ai-consult-provider', true, i)); });
    menu.appendChild(list);
    menu.appendChild(fallbackBlock(ctx, 'ai-consult-fallback', 'ai-consult-floating-fallback-title'));
    var foot = el('div', { class: 'ai-consult-menu__foot' });
    if (cfg.contactUrl) foot.appendChild(el('a', { class: 'ai-consult-menu__direct', href: cfg.contactUrl, 'data-hover': true, text: copy.directLinkLabel }));
    foot.appendChild(el('p', { text: copy.disclaimer }));
    // プライバシー文はフォームのヒント行と重複するため、自由記入欄が無いときだけ出す
    if (!hasNote(ctx.prompts[promptId])) foot.appendChild(el('p', { text: fmt(copy.privacyNote, { siteName: cfg.siteName }) }));
    menu.appendChild(foot);
    root.appendChild(menu);
    var btn = el('button', { class: 'ai-consult-toggle', type: 'button', 'aria-expanded': 'false', 'aria-controls': menuId, 'data-ai-consult-toggle': true, 'data-hover': true });
    var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24'); svg.setAttribute('width', '18'); svg.setAttribute('height', '18'); svg.setAttribute('aria-hidden', 'true');
    var pathEl = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    pathEl.setAttribute('d', 'M12 2.75c.45 5.05 3.2 7.8 8.25 8.25-5.05.45-7.8 3.2-8.25 8.25-.45-5.05-3.2-7.8-8.25-8.25C8.8 10.55 11.55 7.8 12 2.75Z');
    svg.appendChild(pathEl); btn.appendChild(svg);
    btn.appendChild(el('span', { text: copy.buttonLabel }));
    btn.appendChild(el('span', { class: 'ai-consult-toggle__mark', 'aria-hidden': 'true', text: '＋' }));
    root.appendChild(btn);
    root.appendChild(el('p', { class: 'ai-consult-sr-only', 'aria-live': 'polite', 'data-ai-consult-live': true }));
    applyTheme(root, cfg.theme);
    document.body.appendChild(root);
    var api = wire(root, ctx) || {};

    // 初期展開(PC向け): 一定時間後にメニューを開いて見せる。セッション中1回だけ。フォーカスは奪わない
    var ao = f.autoOpen;
    var aoExcluded = !!(ao && (ao.excludePaths || []).some(function (p2) { p2 = normalizePath(p2); return path === p2 || path.indexOf(p2 + '/') === 0; }));
    if (ao && ao.enabled !== false && api.openMenu && !aoExcluded) {
      var minW = ao.minWidth || 1024;
      var okWidth = ao.desktopOnly === false ? true : global.innerWidth >= minW;
      var key = ao.storageKey || 'aic_auto_opened';
      var seen = false;
      try { seen = (ao.oncePerSession !== false) && global.sessionStorage.getItem(key) === '1'; } catch (e) { seen = false; }
      if (okWidth && !seen && !(global.matchMedia && global.matchMedia('(prefers-reduced-motion: reduce)').matches && ao.respectReducedMotion !== false)) {
        global.setTimeout(function () {
          if (document.hidden) return;
          // サイト側のモーダル等が出ている間は自動展開しない(次のページ表示で再試行)
          if (ao.suppressWhen) {
            var blocker = document.querySelector(ao.suppressWhen);
            if (blocker && getComputedStyle(blocker).visibility !== 'hidden' && getComputedStyle(blocker).display !== 'none') return;
          }
          api.openMenu(true);
          try { global.sessionStorage.setItem(key, '1'); } catch (e) { /* noop */ }
          if (ao.autoCloseMs) global.setTimeout(function () { api.closeMenu(); }, ao.autoCloseMs);
        }, ao.delayMs != null ? ao.delayMs : 1500);
      }
    }
    // 他の固定要素(サイト独自のポップアップ等)と重なる時に上へ逃がす
    if (f.avoid && f.avoid.selector) {
      // メニューを開いている間は逃がし分を解放し、重なる相手を一時的に隠す(既定: 解放する)
      var release = f.avoid.releaseOnOpen !== false;
      var update = function () {
        var target = document.querySelector(f.avoid.selector);
        var visible = !!target && !target.hidden && getComputedStyle(target).display !== 'none' && (!f.avoid.visibleClass || target.classList.contains(f.avoid.visibleClass));
        var mobile = !f.avoid.mobileOnly || global.innerWidth <= (f.avoid.maxWidth || 767);
        var menuEl = root.querySelector('[data-ai-consult-menu]');
        var open = !!menuEl && !menuEl.hidden;
        var duck = release && open && visible && mobile;
        root.style.setProperty('--aic-avoid-offset', (visible && mobile && !duck) ? ((f.avoid.offsetPx || 96) + 'px') : '0px');
        if (target) { // 同値の再設定でMutationObserverが再帰しないよう、変化時だけ触る
          if (duck && !target.hasAttribute('data-aic-avoid-ducked')) target.setAttribute('data-aic-avoid-ducked', '');
          else if (!duck && target.hasAttribute('data-aic-avoid-ducked')) target.removeAttribute('data-aic-avoid-ducked');
        }
      };
      update();
      global.addEventListener('resize', update);
      if (global.MutationObserver) new MutationObserver(update).observe(document.body, { attributes: true, subtree: true, childList: true, attributeFilter: ['class', 'hidden', 'style'] });
    }
    return root;
  }

  function renderInline(ctx, host) {
    var cfg = ctx.cfg, copy = ctx.copy;
    var promptId = host.dataset.promptId || 'default';
    var subject = host.dataset.subject || cfg.siteName;
    var headingId = 'ai-consult-inline-heading-' + promptId + '-' + (++uid);
    var sec = el('section', { class: 'ai-consult-inline', 'aria-labelledby': headingId, 'data-ai-consult-root': true, 'data-prompt-id': promptId, 'data-placement': 'inline' });
    if (host.dataset.promptText) sec.dataset.promptText = host.dataset.promptText;
    if (host.dataset.promptIntro) sec.dataset.promptIntro = host.dataset.promptIntro;
    if (host.dataset.promptAsk) sec.dataset.promptAsk = host.dataset.promptAsk;
    sec.appendChild(el('p', { class: 'ai-consult-inline__eyebrow', text: copy.inlineEyebrow }));
    sec.appendChild(el('h3', { id: headingId, text: host.dataset.heading || fmt(copy.inlineHeading, { subject: subject }) }));
    sec.appendChild(el('p', { class: 'ai-consult-inline__lead', text: host.dataset.lead || copy.inlineLead }));
    sec.appendChild(el('div', { class: 'ai-consult-inline__form', 'data-ai-consult-form-slot': true }));
    var ul = el('ul', { class: 'ai-consult-inline__providers', 'aria-label': copy.menuAria });
    ctx.providers.forEach(function (p, i) { ul.appendChild(el('li', null, [providerLink(ctx, p, promptId, 'inline', 'ai-consult-inline__provider', false, i)])); });
    sec.appendChild(ul);
    sec.appendChild(fallbackBlock(ctx, 'ai-consult-inline__fallback', 'ai-consult-inline-fallback-title-' + promptId));
    var notes = el('div', { class: 'ai-consult-inline__notes' }, [el('p', { text: copy.disclaimer })]);
    if (!hasNote(ctx.prompts[promptId]) && !host.dataset.promptText) notes.appendChild(el('p', { text: fmt(copy.privacyNote, { siteName: cfg.siteName }) }));
    sec.appendChild(notes);
    sec.appendChild(el('p', { class: 'ai-consult-sr-only', 'aria-live': 'polite', 'data-ai-consult-live': true }));
    applyTheme(sec, cfg.theme);
    host.innerHTML = ''; host.appendChild(sec);
    wire(sec, ctx);
    return sec;
  }

  function applyTheme(node, theme) {
    if (!theme) return;
    var map = { ink: '--aic-ink', paper: '--aic-paper', accent: '--aic-accent', accentSoft: '--aic-accent-soft', line: '--aic-line', muted: '--aic-muted', gradient: '--aic-grad', fontBody: '--aic-font-body', fontHeading: '--aic-font-heading', fontMono: '--aic-font-mono', zIndex: '--aic-z' };
    Object.keys(map).forEach(function (k) { if (theme[k] != null) node.style.setProperty(map[k], String(theme[k])); });
  }

  function init(cfg) {
    cfg = cfg || global.AiConsultConfig || {};
    if (!cfg.siteName || !cfg.officialUrl) { console.warn('[ai-consult] siteName と officialUrl は必須です'); return null; }
    var copy = Object.assign({}, DEFAULT_COPY, cfg.copy || {});
    var order = cfg.providers && cfg.providers.length ? cfg.providers : DEFAULT_ORDER;
    var providers = order.map(function (p) {
      if (typeof p === 'string') return DEFAULT_PROVIDERS[p];
      return Object.assign({}, DEFAULT_PROVIDERS[p.id] || {}, p);
    }).filter(Boolean);
    var prompts = Object.assign({}, cfg.prompts || {});
    if (!prompts.default) prompts.default = buildDefaultPrompt({ siteName: cfg.siteName, officialUrl: cfg.officialUrl, contactUrl: cfg.contactUrl, ask: cfg.defaultAsk, blanks: cfg.defaultBlanks, contactLabel: cfg.contactLabel });
    var ctx = { cfg: cfg, copy: copy, providers: providers, prompts: prompts, track: makeTracker(cfg) };
    var out = { floating: null, inline: [] };
    var run = function () {
      out.floating = renderFloating(ctx);
      Array.prototype.forEach.call(document.querySelectorAll('[data-ai-consult-inline]'), function (h) { out.inline.push(renderInline(ctx, h)); });
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run); else run();
    return out;
  }

  global.AiConsult = { init: init, buildProviderUrl: buildProviderUrl, buildDefaultPrompt: buildDefaultPrompt, resolvePrompt: resolvePrompt, DEFAULT_PROVIDERS: DEFAULT_PROVIDERS, DEFAULT_COPY: DEFAULT_COPY, version: '2.0.0' };
  if (global.AiConsultConfig && global.AiConsultConfig.autoInit !== false) init(global.AiConsultConfig);
})(window);
